import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Checkbox,
  Button,
  Calendar,
  Badge,
  message,
  Typography,
  Tooltip,
  Layout,
  List,
  Avatar,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { eventService, Event } from '../services/events';
import { taskService, taskTimelogService, Task, TaskReview, TaskTimelog } from '../services/tasks';
import { useNavigate } from 'react-router-dom';
import { customerService, Customer } from '../services/customers';
import { businessService, Business } from '../services/businesses';
import { staffService, Staff } from '../services/staffs';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { getDeadlineSettings } from '../utils/deadlineSettings';

const { Title } = Typography;
const { Sider, Content } = Layout;

type CalendarView = 'month' | 'week' | 'day';

type CalendarItem = {
  id: number | string;
  customer_name: string;
  location?: string;
  type: Event['type'];
  all_day: boolean;
  start: string;
  end: string;
  alert: boolean;
  attachment?: string;
  note?: string;
  staff_id?: number | null;
  staff?: Event['staff'] | null;
  task?: Task;
};

export const CalendarPage: React.FC = () => {
  const [entityType, setEntityType] = useState<'customer' | 'staff'>('customer');
  const [events, setEvents] = useState<CalendarItem[]>([]);
  const [timelogs, setTimelogs] = useState<TaskTimelog[]>([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [staffsLoading, setStaffsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [searchText, setSearchText] = useState('');
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<CalendarView>('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventFilters, setEventFilters] = useState<Record<string, boolean>>({
    appointment: true,
    task: true,
    leave: true,
    deadline: true,
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewCalendar = hasPermission(user, 'calendar.view');
  const canViewPersonal = hasPermission(user, 'personal.view');
  const canViewStaff = hasPermission(user, 'staff.view');
  const canViewBusiness = hasPermission(user, 'business.view');
  const canCreate = hasPermission(user, 'calendar.create');

  useEffect(() => {
    if (entityType === 'customer') {
      if (!canViewPersonal) {
        setCustomers([]);
        setSelectedCustomer(null);
        return;
      }
      loadCustomers();
    } else {
      if (!canViewStaff) {
        setStaffs([]);
        setSelectedStaff(null);
        return;
      }
      loadStaffs();
    }
  }, [entityType, canViewPersonal, canViewStaff]);

  useEffect(() => {
    if (!canViewBusiness) {
      setBusinesses([]);
      return;
    }
    loadBusinesses();
  }, [canViewBusiness]);

  useEffect(() => {
    if (!canViewCalendar) {
      setEvents([]);
      setTimelogs([]);
      return;
    }
    loadEvents();
  }, [currentDate, view, selectedCustomer, selectedStaff, canViewCalendar, customers, businesses]);

  const loadCustomers = async () => {
    if (!canViewPersonal) {
      setCustomers([]);
      return;
    }
    setCustomersLoading(true);
    try {
      const response = await customerService.getAll();
      setCustomers(response.data || []);
    } catch {
      message.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  const loadStaffs = async () => {
    if (!canViewStaff) {
      setStaffs([]);
      return;
    }
    setStaffsLoading(true);
    try {
      const response = await staffService.getAll();
      setStaffs(Array.isArray(response) ? response : []);
    } catch {
      message.error('Failed to load staffs');
    } finally {
      setStaffsLoading(false);
    }
  };

  const loadBusinesses = async () => {
    if (!canViewBusiness) {
      setBusinesses([]);
      return;
    }
    try {
      const response = await businessService.getAll();
      setBusinesses(response.data || []);
    } catch {
      message.error('Failed to load businesses');
    } finally {
      // no-op
    }
  };

  const loadEvents = async () => {
    if (!canViewCalendar) {
      setEvents([]);
      setTimelogs([]);
      return;
    }
    setLoading(true);
    try {
      const [eventsResponse, timelogsResponse, tasksResponse] = await Promise.all([
        eventService.getAll(),
        taskTimelogService.getAll().catch(() => ({ data: [] })),
        taskService.getAll().catch(() => ({ data: [] })),
      ]);
      const eventsData = (eventsResponse.data || []).filter((event: Event) => event.type !== 'task');
      const taskEvents = buildTaskEvents(tasksResponse.data || []);
      const deadlineEvents = buildDeadlineEvents(customers, businesses);
      let combinedEvents: CalendarItem[] = [...eventsData, ...taskEvents, ...deadlineEvents];
      
      // Filter events by selected entity
      if (entityType === 'customer' && selectedCustomer) {
        combinedEvents = combinedEvents.filter((event: CalendarItem) => 
          event.customer_name === selectedCustomer.name
        );
      } else if (entityType === 'staff' && selectedStaff) {
        combinedEvents = combinedEvents.filter((event: CalendarItem) => {
          const staffId = event.staff_id ?? event.task?.staff_id;
          return staffId === selectedStaff.id;
        });
      }
      
      setEvents(combinedEvents);
      setTimelogs(timelogsResponse.data || []);
    } catch {
      message.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const getCustomerInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredStaffs = staffs.filter((staff) =>
    staff.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleDateChange = (date: Dayjs) => {
    setCurrentDate(date);
  };

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'));
  };

  const handleFilterChange = (type: string, checked: boolean) => {
    setEventFilters(prev => ({ ...prev, [type]: checked }));
  };

  const filteredEvents = events.filter(event => {
    if (searchQuery && !event.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return eventFilters[event.type] !== false;
  });

  const getListData = (value: Dayjs) => {
    return filteredEvents.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const valueDate = value.format('YYYY-MM-DD');
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      return valueDate >= startDate && valueDate <= endDate;
    });
  };

  const calculateDailyTimeCost = (value: Dayjs): number => {
    const valueDate = value.format('YYYY-MM-DD');
    let totalHours = 0;

    // Calculate time from events (non-all-day events)
    filteredEvents.forEach(event => {
      if (event.type === 'task') {
        return;
      }
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      
      if (valueDate >= startDate && valueDate <= endDate) {
        if (event.all_day) {
          // All-day events count as 8 hours
          totalHours += 8;
        } else {
          // Calculate actual hours for this specific day
          if (valueDate === startDate && valueDate === endDate) {
            // Same day event
            const hours = eventEnd.diff(eventStart, 'hour', true);
            totalHours += hours;
          } else if (valueDate === startDate) {
            // First day of multi-day event
            const endOfDay = eventStart.endOf('day');
            const hours = endOfDay.diff(eventStart, 'hour', true);
            totalHours += hours;
          } else if (valueDate === endDate) {
            // Last day of multi-day event
            const startOfDay = eventEnd.startOf('day');
            const hours = eventEnd.diff(startOfDay, 'hour', true);
            totalHours += hours;
          } else {
            // Middle day of multi-day event (full day)
            totalHours += 24;
          }
        }
      }
    });

    // Add time from task timelogs
    timelogs.forEach(timelog => {
      const timelogDate = dayjs(timelog.date).format('YYYY-MM-DD');
      if (timelogDate === valueDate && timelog.enabled) {
        totalHours += Number(timelog.hour) || 0;
      }
    });

    return totalHours;
  };

  const calculateDailyTaskHours = (value: Dayjs): number => {
    const valueDate = value.format('YYYY-MM-DD');
    return timelogs.reduce((total, timelog) => {
      const timelogDate = dayjs(timelog.date).format('YYYY-MM-DD');
      if (timelogDate === valueDate && timelog.enabled) {
        return total + (Number(timelog.hour) || 0);
      }
      return total;
    }, 0);
  };

  const buildTaskEvents = (tasks: Task[]): CalendarItem[] => {
    return tasks.map((task) => {
      const taskDate = getTaskEventDate(task);
      const dateValue = taskDate ?? dayjs();
      const start = dateValue.startOf('day').format('YYYY-MM-DD HH:mm:ss');
      const end = dateValue.endOf('day').format('YYYY-MM-DD HH:mm:ss');
      return {
        id: `task-${task.id}`,
        customer_name: getTaskDisplayName(task),
        type: 'task',
        all_day: true,
        start,
        end,
        alert: false,
        staff_id: task.staff_id ?? null,
        staff: task.staff ? { id: task.staff.id, name: task.staff.name } : null,
        task,
      };
    });
  };

  const buildDeadlineEvents = (customersData: Customer[], businessesData: Business[]): CalendarItem[] => {
    const settings = getDeadlineSettings();
    const referenceYear = currentDate.year();
    const deadlineEvents: CalendarItem[] = [];

    if (canViewPersonal) {
      customersData.forEach((customer) => {
        const engagementTokens = (customer.engagement || '')
          .split(',')
          .map((token) => token.trim().toUpperCase())
          .filter(Boolean);
        if (engagementTokens.includes('B')) {
          deadlineEvents.push(
            buildPersonalDeadlineEvent(customer, 'B', settings.personal.b.month, settings.personal.b.day, referenceYear),
          );
        }
        if (engagementTokens.includes('BE')) {
          deadlineEvents.push(
            buildPersonalDeadlineEvent(customer, 'BE', settings.personal.be.month, settings.personal.be.day, referenceYear),
          );
        }
      });
    }

    if (canViewBusiness) {
      businessesData.forEach((business) => {
        const yearEndDate = business.year_end ? dayjs(business.year_end) : null;
        if (yearEndDate) {
          const yearEnd = yearEndDate.year(referenceYear);
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Account', yearEnd.add(settings.business.accountOffsetMonths, 'month')),
          );
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Audit', yearEnd.add(settings.business.auditOffsetMonths, 'month')),
          );
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Tax', yearEnd.add(settings.business.taxOffsetMonths, 'month')),
          );
        }
        if (business.incorporation_date) {
          const incorporation = dayjs(business.incorporation_date).year(referenceYear);
          deadlineEvents.push(
            buildBusinessDeadlineEvent(
              business,
              'Annual Return',
              incorporation.add(settings.business.annualReturnOffsetYears, 'year'),
            ),
          );
        }
      });
    }

    return deadlineEvents.filter(Boolean);
  };

  const buildPersonalDeadlineEvent = (
    customer: Customer,
    label: string,
    month: number,
    day: number,
    year: number,
  ): CalendarItem => {
    const dueDate = dayjs().year(year).month(month - 1).date(day);
    return {
      id: `deadline-personal-${customer.id}-${label}-${year}`,
      customer_name: customer.name,
      type: 'deadline',
      all_day: true,
      start: dueDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: dueDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      alert: false,
      note: `Personal ${label} deadline`,
      staff_id: customer.staff_id ?? null,
    };
  };

  const buildBusinessDeadlineEvent = (business: Business, label: string, dueDate: Dayjs): CalendarItem => {
    const entityLabel = business.entity ? ` (${business.entity})` : '';
    return {
      id: `deadline-business-${business.id}-${label}-${dueDate.format('YYYY')}`,
      customer_name: business.name,
      type: 'deadline',
      all_day: true,
      start: dueDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: dueDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      alert: false,
      note: `Business ${label} deadline${entityLabel}`,
      staff_id: business.staff_id ?? null,
    };
  };

  const getTaskDisplayName = (task: Task): string => {
    return task.customer?.name || `Task #${task.id}`;
  };

  const getTaskEventDate = (task: Task): Dayjs | null => {
    const candidate = task.plan_date || task.deadline || task.due_date || task.created_date || task.created_at;
    return candidate ? dayjs(candidate) : null;
  };

  const formatTaskDate = (value?: string | null) => {
    return value ? dayjs(value).format('DD/MM/YYYY') : '-';
  };

  const getLatestReviewDate = (reviews: TaskReview[] | undefined, predicate: (review: TaskReview) => boolean) => {
    if (!reviews || reviews.length === 0) return undefined;
    const filteredReviews = reviews.filter(predicate);
    if (filteredReviews.length === 0) return undefined;
    return filteredReviews.reduce((latest, review) => {
      if (!latest) {
        return review.created_at;
      }
      return dayjs(review.created_at).isAfter(dayjs(latest)) ? review.created_at : latest;
    }, filteredReviews[0].created_at as string);
  };

  const dateCellRender = (value: Dayjs) => {
    const listData = getListData(value);
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {listData.map((event) => (
          <li key={event.id} style={{ marginBottom: 4 }}>
            <Tooltip title={renderEventTooltip(event)} placement="bottom" overlayStyle={{ maxWidth: 300 }}>
              <span
                style={{
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '2px',
                  backgroundColor: getEventTypeColor(event.type).bg,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                onClick={() => handleEventClick(event)}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: getEventTypeColor(event.type).dot,
                  }}
                />
                {event.customer_name}
              </span>
            </Tooltip>
          </li>
        ))}
      </ul>
    );
  };

  const dateFullCellRender = (value: Dayjs) => {
    const listData = getListData(value);
    const totalHours = calculateDailyTimeCost(value);
    const taskHours = calculateDailyTaskHours(value);
    const isCurrentMonth = value.month() === currentDate.month();
    
    return (
      <div className="ant-picker-cell-inner" style={{ position: 'relative', height: '100%', minHeight: '80px', width: '100%' }}>
        <div 
          className="ant-picker-calendar-date"
          style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 8px',
            marginBottom: '4px',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <span 
            className="ant-picker-calendar-date-value"
            style={{ 
              color: isCurrentMonth ? undefined : '#bfbfbf'
            }}
          >
            {value.date()}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            {taskHours > 0 && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#52c41a',
                  borderRadius: 4,
                  padding: '0 6px',
                  lineHeight: '16px',
                }}
              >
                {taskHours.toFixed(1)}h
              </span>
            )}
          </div>
        </div>
        <div className="ant-picker-calendar-date-content" style={{ padding: '0 4px', width: '100%', boxSizing: 'border-box' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {listData.map((event) => (
              <li key={event.id} style={{ marginBottom: 4 }}>
                <Tooltip title={renderEventTooltip(event)} placement="bottom" overlayStyle={{ maxWidth: 300 }}>
                  <span
                    style={{
                      fontSize: '12px',
                      cursor: canViewCalendar ? 'pointer' : 'default',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      backgroundColor: getEventTypeColor(event.type).bg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={canViewCalendar ? () => handleEventClick(event) : undefined}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: getEventTypeColor(event.type).dot,
                      }}
                    />
                    {event.customer_name}
                  </span>
                </Tooltip>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const handleEventClick = (event: CalendarItem) => {
    if (!canViewCalendar) {
      return;
    }
    if (event.task || event.type === 'deadline') {
      return;
    }
    navigate(`/calendar/events/${event.id}/edit`);
  };

  const handleAddEvent = () => {
    navigate('/calendar/events/new');
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'appointment':
        return { bg: '#e6f7ff', dot: '#1890ff' };
      case 'task':
        return { bg: '#fff7e6', dot: '#fa8c16' };
      case 'leave':
        return { bg: '#f6ffed', dot: '#52c41a' };
      case 'deadline':
        return { bg: '#fff1f0', dot: '#ff4d4f' };
      default:
        return { bg: '#f5f5f5', dot: '#8c8c8c' };
    }
  };

  const renderEventTooltip = (event: CalendarItem) => {
    if (event.task) {
      const task = event.task;
      const createdDate = task.created_date || task.created_at;
      const planDate = task.plan_date;
      const reviewDate = getLatestReviewDate(task.reviews, (review) => !review.customer_id);
      const confirmDate = getLatestReviewDate(task.reviews, (review) => Boolean(review.customer_id));
      const completedDate = task.status === 'Completed' || task.is_completed ? task.updated_at : undefined;
      return (
        <div style={{ maxWidth: 260 }}>
          <div style={{ fontWeight: 500, marginBottom: 6, fontSize: '14px' }}>{event.customer_name}</div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Created: {formatTaskDate(createdDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Plan: {formatTaskDate(planDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Review: {formatTaskDate(reviewDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Confirm: {formatTaskDate(confirmDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Complete: {formatTaskDate(completedDate)}
          </div>
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 250 }}>
        <div style={{ fontWeight: 500, marginBottom: 6, fontSize: '14px' }}>{event.customer_name}</div>
        {event.location && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            📍 {event.location}
          </div>
        )}
        <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
          {event.all_day 
            ? 'All Day Event' 
            : `${dayjs(event.start).format('MMM D, YYYY HH:mm')} - ${dayjs(event.end).format('MMM D, YYYY HH:mm')}`
          }
        </div>
        <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
          Type: <span style={{ textTransform: 'capitalize' }}>{event.type}</span>
        </div>
        {event.staff?.name && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Staff: {event.staff.name}
          </div>
        )}
        {event.alert && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            🔔 Alert Enabled
          </div>
        )}
        {event.note && (
          <div style={{ 
            fontSize: '12px', 
            marginTop: 6, 
            borderTop: '1px solid rgba(255,255,255,0.3)', 
            paddingTop: 6,
            color: '#fff'
          }}>
            {event.note}
          </div>
        )}
      </div>
    );
  };

  const getMonthLabel = () => {
    if (view === 'month') {
      return currentDate.format('MMMM YYYY');
    } else if (view === 'week') {
      const weekStart = currentDate.startOf('week');
      const weekEnd = currentDate.endOf('week');
      return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
    } else {
      return currentDate.format('MMMM D, YYYY');
    }
  };

  const getWeekDays = () => {
    const weekStart = currentDate.startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(weekStart.clone().add(i, 'day'));
    }
    return days;
  };

  const getDayHours = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push(i);
    }
    return hours;
  };

  const getEventsForDate = (date: Dayjs) => {
    return filteredEvents.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const dateStr = date.format('YYYY-MM-DD');
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  const getEventsForHour = (date: Dayjs, hour: number) => {
    return filteredEvents.filter(event => {
      if (event.type === 'task') return false;
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const dateStr = date.format('YYYY-MM-DD');
      const eventDate = eventStart.format('YYYY-MM-DD');
      
      if (dateStr !== eventDate) return false;
      
      const eventHour = eventStart.hour();
      const eventEndHour = eventEnd.hour();
      
      if (event.all_day) return true;
      
      return hour >= eventHour && hour <= eventEndHour;
    });
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays();
    
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', gap: '1px', backgroundColor: '#f0f0f0' }}>
          <div style={{ padding: '8px', backgroundColor: '#fff', fontWeight: 500 }}></div>
          {weekDays.map((day, index) => {
            const isToday = day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
            return (
              <div 
                key={index} 
                style={{ 
                  padding: '8px', 
                  backgroundColor: isToday ? '#e6f7ff' : '#fff', 
                  textAlign: 'center', 
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={() => setCurrentDate(day)}
              >
                <div>{day.format('ddd')}</div>
                <div style={{ fontSize: '18px', marginTop: '4px' }}>{day.format('D')}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', gap: '1px', backgroundColor: '#f0f0f0' }}>
          {getDayHours().map(hour => (
            <React.Fragment key={hour}>
              <div style={{ padding: '8px', backgroundColor: '#fff', textAlign: 'right', fontSize: '12px', color: '#999' }}>
                {hour.toString().padStart(2, '0')}:00
              </div>
              {weekDays.map((day, dayIndex) => {
                const hourEvents = getEventsForHour(day, hour);
                return (
                  <div key={dayIndex} style={{ padding: '4px', backgroundColor: '#fff', minHeight: '60px' }}>
                    {hourEvents.map(event => (
                      <Tooltip key={event.id} title={renderEventTooltip(event)} placement="bottom" overlayStyle={{ maxWidth: 300 }}>
                        <div
                          style={{
                            fontSize: '11px',
                            padding: '2px 4px',
                            marginBottom: '2px',
                            borderRadius: '2px',
                            backgroundColor: getEventTypeColor(event.type).bg,
                            borderLeft: `3px solid ${getEventTypeColor(event.type).dot}`,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleEventClick(event)}
                        >
                          {event.customer_name}
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = getDayHours();
    const dayEvents = getEventsForDate(currentDate);
    
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1px', backgroundColor: '#f0f0f0' }}>
          <div style={{ padding: '8px', backgroundColor: '#fff', fontWeight: 500 }}>Time</div>
          <div style={{ padding: '8px', backgroundColor: '#fff', fontWeight: 500 }}>Events</div>
        </div>
        {hours.map(hour => {
          const hourEvents = getEventsForHour(currentDate, hour);
          return (
            <div key={hour} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1px', backgroundColor: '#f0f0f0' }}>
              <div style={{ padding: '8px', backgroundColor: '#fff', textAlign: 'right', fontSize: '14px' }}>
                {hour.toString().padStart(2, '0')}:00
              </div>
                <div style={{ padding: '8px', backgroundColor: '#fff', minHeight: '80px' }}>
                  {hourEvents.map(event => (
                    <Tooltip key={event.id} title={renderEventTooltip(event)} placement="right" overlayStyle={{ maxWidth: 300 }}>
                      <div
                        style={{
                          padding: '8px',
                          marginBottom: '8px',
                          borderRadius: '4px',
                          backgroundColor: getEventTypeColor(event.type).bg,
                          borderLeft: `4px solid ${getEventTypeColor(event.type).dot}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleEventClick(event)}
                      >
                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>{event.customer_name}</div>
                        {event.location && <div style={{ fontSize: '12px', color: '#666' }}>📍 {event.location}</div>}
                        {!event.all_day && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {dayjs(event.start).format('HH:mm')} - {dayjs(event.end).format('HH:mm')}
                          </div>
                        )}
                        {event.note && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{event.note}</div>}
                      </div>
                    </Tooltip>
                  ))}
                </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout style={{ height: 'calc(100vh - 112px)', background: '#fff', overflow: 'hidden', display: 'flex', margin: '-24px' }}>
      <Sider
        width={300}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          paddingRight: '16px',
          paddingTop: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
          <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>Calendar</Title>
          <Radio.Group
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setSelectedCustomer(null);
              setSelectedStaff(null);
            }}
            style={{ marginBottom: 16, width: '100%' }}
          >
            <Radio.Button value="customer" style={{ width: '50%', textAlign: 'center' }}>
              Personal
            </Radio.Button>
            <Radio.Button value="staff" style={{ width: '50%', textAlign: 'center' }}>
              Staff
            </Radio.Button>
          </Radio.Group>
          <Input
            placeholder={`Search ${entityType === 'customer' ? 'Customer' : 'Staff'}`}
            prefix={<SearchOutlined />}
            style={{ marginBottom: 16 }}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />
        </div>
        
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px', minHeight: 0 }}>
          {entityType === 'customer' ? (
            <List
              loading={customersLoading}
              dataSource={filteredCustomers}
              pagination={false}
              style={{ minHeight: 0 }}
              renderItem={(customer) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: 4,
                    backgroundColor:
                      selectedCustomer?.id === customer.id ? '#e6f7ff' : 'transparent',
                    marginBottom: 8,
                  }}
                  onClick={() => setSelectedCustomer(customer)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: '#1890ff',
                          color: '#fff',
                        }}
                      >
                        {getCustomerInitials(customer.name)}
                      </Avatar>
                    }
                    title={customer.name}
                  />
                </List.Item>
              )}
            />
          ) : (
            <List
              loading={staffsLoading}
              dataSource={filteredStaffs}
              pagination={false}
              style={{ minHeight: 0 }}
              renderItem={(staff) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: 4,
                    backgroundColor:
                      selectedStaff?.id === staff.id ? '#e6f7ff' : 'transparent',
                    marginBottom: 8,
                  }}
                  onClick={() => setSelectedStaff(staff)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: '#52c41a',
                          color: '#fff',
                        }}
                      >
                        {getCustomerInitials(staff.name)}
                      </Avatar>
                    }
                    title={staff.name}
                  />
                </List.Item>
              )}
            />
          )}
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid #f0f0f0', padding: '16px' }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Event Types</div>
            <Checkbox
              checked={eventFilters.appointment}
              onChange={(e) => handleFilterChange('appointment', e.target.checked)}
              style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#1890ff' 
                }} />
                Appointment
              </span>
            </Checkbox>
            <Checkbox
              checked={eventFilters.task}
              onChange={(e) => handleFilterChange('task', e.target.checked)}
              style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#fa8c16' 
                }} />
                Task
              </span>
            </Checkbox>
            <Checkbox
              checked={eventFilters.leave}
              onChange={(e) => handleFilterChange('leave', e.target.checked)}
              style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#52c41a' 
                }} />
                Leave
              </span>
            </Checkbox>
            <Checkbox
              checked={eventFilters.deadline}
              onChange={(e) => handleFilterChange('deadline', e.target.checked)}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: '#ff4d4f' 
                }} />
                Deadline
              </span>
            </Checkbox>
          </div>
      </Sider>
      <Content 
        style={{ 
          flex: 1,
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
        <Card style={{ border: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Button icon={<LeftOutlined />} onClick={handlePrevMonth} />
              <Title level={4} style={{ margin: 0, minWidth: 200, textAlign: 'center' }}>
                {getMonthLabel()}
              </Title>
              <Button icon={<RightOutlined />} onClick={handleNextMonth} />
            </div>
            
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type={view === 'month' ? 'primary' : 'default'}
                onClick={() => setView('month')}
              >
                Month
              </Button>
              <Button
                type={view === 'week' ? 'primary' : 'default'}
                onClick={() => setView('week')}
              >
                Week
              </Button>
              <Button
                type={view === 'day' ? 'primary' : 'default'}
                onClick={() => setView('day')}
              >
                Day
              </Button>
            </div>

            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEvent}>
                Add Events
              </Button>
            )}
          </div>

          {view === 'month' && (
            <Calendar
              value={currentDate}
              onChange={handleDateChange}
              dateFullCellRender={dateFullCellRender}
              mode="month"
            />
          )}
          {view === 'week' && renderWeekView()}
          {view === 'day' && renderDayView()}
        </Card>
        </div>
      </Content>
    </Layout>
  );
};
